/**
 * The standard Guide entry at the foot of every module's left nav, mounted
 * once by AppShell (Sasha, 2026-08-16: "we need to standardize how we show
 * help info for each module").
 *
 * It NAVIGATES to the module's guide page rather than opening a dialog. The
 * first cut opened one article in a modal, which was the wrong shape twice
 * over: a module's documentation is a set, not a page, and a modal cannot be
 * linked to. `/sales/guide` shows the whole set and can be sent to someone.
 *
 * Renders nothing when a module has no published documentation, so a module
 * nobody has written for shows no dead entry. That check is the same list the
 * guide page loads, so opening it costs nothing extra.
 */

import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { BookQuestionMarkRegular } from '@fluentui/react-icons';
import { bffFetch } from '../../../api/bffFetch';

/** Module -> does it have documentation. Cached for the session: the shell
 *  mounts on every page, and the answer changes when someone authors, not
 *  while someone browses. */
const hasGuideCache = new Map<string, boolean>();

async function moduleHasGuide(module: string): Promise<boolean> {
  if (hasGuideCache.has(module)) return hasGuideCache.get(module) ?? false;
  try {
    const res = await bffFetch(
      `/api/academy/content?kind=article&allTypes=1&module=${encodeURIComponent(module)}`
    );
    if (!res.ok) return false; // unauthenticated / transient: hide the entry
    const body = (await res.json()) as { items: unknown[] };
    const has = (body.items ?? []).length > 0;
    hasGuideCache.set(module, has);
    return has;
  } catch {
    return false;
  }
}

export interface ModuleGuideNavItemProps {
  /** Module id as tagged on the articles ('sales', 'portfolio', ...). */
  module: string;
  /** Where the module is mounted ('/sales'); the guide lives under it. */
  basePath: string;
  /** Collapsed sidebar: icon only, label carried by the title attribute. */
  collapsed?: boolean;
  /** The shell's own nav classes, so this matches every other entry. */
  className?: string;
  activeClassName?: string;
  iconClassName?: string;
}

export function ModuleGuideNavItem({
  module,
  basePath,
  collapsed = false,
  className,
  activeClassName,
  iconClassName,
}: ModuleGuideNavItemProps) {
  const [has, setHas] = useState(false);

  useEffect(() => {
    let alive = true;
    moduleHasGuide(module).then((h) => alive && setHas(h));
    return () => {
      alive = false;
    };
  }, [module]);

  if (!has) return null;

  return (
    <NavLink
      to={`${basePath}/guide`}
      title="Guide"
      className={({ isActive }) =>
        isActive && activeClassName ? `${className ?? ''} ${activeClassName}` : className ?? ''
      }
    >
      <BookQuestionMarkRegular className={iconClassName} />
      {!collapsed && <span>Guide</span>}
    </NavLink>
  );
}

export default ModuleGuideNavItem;
